const ERROR_SELECTOR = '[data-validation-error], [data-slot="field"][data-invalid="true"]';
const MESSAGE_SELECTOR = '[data-validation-error], [data-slot="field-error"]';
const FIELD_SELECTOR = '[data-validation-field], [data-slot="field"]';
const CONTROL_SELECTOR = 'input:not([type="hidden"]), textarea, select, [role="combobox"], [role="switch"], [role="checkbox"], button[aria-haspopup]';
const INSET = 16;
let nextId = 0;

function visible(element: HTMLElement) {
  const style = getComputedStyle(element);
  return !element.closest('[hidden], [inert], [aria-hidden="true"]') &&
    style.display !== 'none' && style.visibility !== 'hidden' && element.getBoundingClientRect().height > 0;
}

function identify(element: HTMLElement) {
  if (!element.id) element.id = `validation-target-${++nextId}`;
  return element.id;
}

function linkErrorMessages(control: HTMLElement, field: HTMLElement, error: HTMLElement) {
  const messages = [...field.querySelectorAll<HTMLElement>(MESSAGE_SELECTOR)];
  if (error !== field && !messages.includes(error) && !error.matches(CONTROL_SELECTOR)) messages.push(error);
  const descriptions = new Set((control.getAttribute('aria-describedby') ?? '').split(' ').filter(Boolean));
  for (const message of messages) {
    if (message !== control) descriptions.add(identify(message));
  }
  if (descriptions.size) control.setAttribute('aria-describedby', [...descriptions].join(' '));
  const label = field.querySelector<HTMLElement>('label, [data-slot="field-label"]');
  if (label && !control.hasAttribute('aria-label') && !control.hasAttribute('aria-labelledby')) {
    control.setAttribute('aria-labelledby', identify(label));
  }
}

function region(field: HTMLElement, control: HTMLElement) {
  const elements = [field, ...field.querySelectorAll<HTMLElement>(MESSAGE_SELECTOR)];
  for (const id of (control.getAttribute('aria-describedby') ?? '').split(' ')) {
    const message = document.getElementById(id);
    if (message) elements.push(message);
  }
  const rects = elements.filter(visible).map((element) => element.getBoundingClientRect());
  return {
    top: Math.min(...rects.map((rect) => rect.top)),
    bottom: Math.max(...rects.map((rect) => rect.bottom)),
    left: Math.min(...rects.map((rect) => rect.left)),
    right: Math.max(...rects.map((rect) => rect.right)),
  };
}

function scrollToTarget(form: HTMLFormElement, field: HTMLElement, control: HTMLElement, behavior: ScrollBehavior) {
  const viewport = window.visualViewport;
  const viewportTop = viewport?.offsetTop ?? 0;
  let bottom = viewportTop + (viewport?.height ?? window.innerHeight);
  let page: HTMLElement | null = form;
  while (page && !page.querySelector('[data-validation-footer]')) page = page.parentElement;
  for (const footer of page?.querySelectorAll<HTMLElement>('[data-validation-footer]') ?? []) {
    if (visible(footer)) bottom = Math.min(bottom, footer.getBoundingClientRect().top);
  }

  let ancestor = field.parentElement;
  let hasVerticalContainer = false;
  while (ancestor && ancestor !== document.body) {
    const style = getComputedStyle(ancestor);
    const bounds = ancestor.getBoundingClientRect();
    const target = region(field, control);
    const top = Math.max(viewportTop, bounds.top);
    const end = Math.min(bottom, bounds.bottom);
    const options: ScrollToOptions = { behavior };
    if (/(auto|scroll)/.test(style.overflowY) &&
      (ancestor.scrollHeight > ancestor.clientHeight || ancestor.clientHeight === 0 || ancestor.hasAttribute('data-validation-scroll'))) {
      hasVerticalContainer = true;
      if (target.top < top || target.bottom > end) options.top = Math.max(0, ancestor.scrollTop + target.top - top - INSET);
    }
    const horizontalTarget = target.right - target.left > bounds.width ? control.getBoundingClientRect() : target;
    if (/(auto|scroll)/.test(style.overflowX) && (horizontalTarget.left < bounds.left || horizontalTarget.right > bounds.right)) {
      options.left = Math.max(0, ancestor.scrollLeft + horizontalTarget.left - bounds.left - INSET);
    }
    if (options.top !== undefined || options.left !== undefined) ancestor.scrollTo(options);
    ancestor = ancestor.parentElement;
  }
  if (!hasVerticalContainer) {
    const target = region(field, control);
    if (target.top < viewportTop || target.bottom > bottom) {
      window.scrollTo({ top: Math.max(0, window.scrollY + target.top - viewportTop - INSET), behavior });
    }
  }
}

export function navigateToFirstError(form: HTMLFormElement): (() => void) | undefined {
  const error = [...form.querySelectorAll<HTMLElement>(ERROR_SELECTOR)]
    .find((element) => element.closest('form') === form && visible(element));
  if (!error) return;
  const field = error.closest<HTMLElement>(FIELD_SELECTOR) ?? error.parentElement ?? error;
  const controls = (error.matches(CONTROL_SELECTOR) ? [error] : [...field.querySelectorAll<HTMLElement>(CONTROL_SELECTOR)])
    .filter((element) => visible(element) && !element.matches(':disabled, [aria-disabled="true"], [readonly]'));
  // A field can hold helper buttons (a phone's country menu) before the invalid input.
  const control = controls.find((element) => element.matches('[aria-invalid="true"]')) ?? controls[0] ?? field;
  linkErrorMessages(control, field, error);
  if (control === field && !control.hasAttribute('tabindex')) control.tabIndex = -1;
  control.focus({ preventScroll: true });
  const behavior: ScrollBehavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth';
  const scroll = () => {
    if (form.isConnected && error.isConnected && error.matches(ERROR_SELECTOR) && visible(error)) scrollToTarget(form, field, control, behavior);
  };
  scroll();
  // Focusing a field on mobile can resize the visual viewport after focus returns.
  window.visualViewport?.addEventListener('resize', scroll);
  const cleanup = () => window.visualViewport?.removeEventListener('resize', scroll);
  const timeout = window.setTimeout(cleanup, 1000);
  return () => { cleanup(); window.clearTimeout(timeout); };
}
